import { useState, useEffect } from 'react';
import { BaseballDiamond } from '@/components/BaseballDiamond';
import Fireworks from '@/components/Fireworks';

/**
 * TV Gameplay Demo - Stadium Jumbotron Style
 * 
 * Visual reference for tvOS development team.
 * Designed to feel like watching a game on a stadium scoreboard.
 */
export default function TVGameplayDemo() {
  const [currentView, setCurrentView] = useState('pitching_waiting');
  const [timeRemaining, setTimeRemaining] = useState(28);
  const [showFireworks, setShowFireworks] = useState(false);
  
  // Demo data
  const demoState = {
    inning: 1,
    half: 'top',
    outs: 1,
    total_innings: 3,
    bases: { first: true, second: false, third: false },
    team_a: { name: 'VISITORS', score: 2 },
    team_b: { name: 'HOME', score: 1 },
    currentBatter: 'SLUGGER'
  };

  // Simulate timer countdown for demo
  useEffect(() => {
    if (currentView === 'song_playing') {
      const interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) return 30;
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [currentView]);

  // Trigger fireworks on HOME RUN or GRAND SLAM view
  useEffect(() => {
    if (currentView === 'result_homerun' || currentView === 'result_grandslam') {
      setShowFireworks(true);
      const timer = setTimeout(() => setShowFireworks(false), 4000);
      return () => clearTimeout(timer);
    } else {
      setShowFireworks(false);
    }
  }, [currentView]);

  const views = [
    { id: 'pitching_waiting', label: 'PITCH' },
    { id: 'song_playing', label: 'PLAYING' },
    { id: 'result_out', label: 'OUT' },
    { id: 'result_single', label: 'SINGLE' },
    { id: 'result_double', label: 'DOUBLE' },
    { id: 'result_triple', label: 'TRIPLE' },
    { id: 'result_homerun', label: 'HOME RUN' },
    { id: 'result_grandslam', label: 'GRAND SLAM' },
    { id: 'inning_summary', label: 'INNING' },
    { id: 'game_over', label: 'FINAL' },
  ];

  // ========== JUMBOTRON SCOREBOARD ==========
  const renderScoreboard = () => (
    <div className="jumbotron-scoreboard px-8 py-4">
      <div className="flex items-center justify-between max-w-6xl mx-auto">
        {/* Team A */}
        <div className="flex items-center gap-6">
          <span className="jumbotron-team text-2xl">{demoState.team_a.name}</span>
          <div className="bg-black px-6 py-3 border-4 border-green-900">
            <span className="jumbotron-score text-6xl">{demoState.team_a.score}</span>
          </div>
        </div>

        {/* Center - Inning & Outs */}
        <div className="text-center">
          <div className="jumbotron-inning text-3xl mb-2">
            {demoState.half === 'top' ? '▲' : '▼'} {demoState.inning}
          </div>
          <div className="flex gap-3 justify-center">
            {[0, 1, 2].map(i => (
              <div 
                key={i}
                className={`jumbotron-out ${i < demoState.outs ? 'filled' : ''}`}
              />
            ))}
          </div>
        </div>

        {/* Team B */}
        <div className="flex items-center gap-6">
          <div className="bg-black px-6 py-3 border-4 border-green-900">
            <span className="jumbotron-score text-6xl">{demoState.team_b.score}</span>
          </div>
          <span className="jumbotron-team text-2xl">{demoState.team_b.name}</span>
        </div>
      </div>
    </div>
  );

  // ========== PITCHING WAITING ==========
  const renderPitchingWaiting = () => (
    <div className="flex-1 flex items-center justify-center gap-12 p-8">
      {/* Left - Pitching Team */}
      <div className="matchup-card p-8 text-center w-80">
        <p className="retro-body text-xl text-dirt mb-2">PITCHING</p>
        <p className="retro-heading text-3xl text-dirt mb-4">
          {demoState.half === 'top' ? demoState.team_b.name : demoState.team_a.name}
        </p>
        <div className="w-24 h-24 mx-auto bg-dirt rounded-full flex items-center justify-center border-4 border-ink">
          <span className="text-4xl">⚾</span>
        </div>
      </div>

      {/* Center - VS & Diamond */}
      <div className="text-center">
        <div className="retro-heading text-6xl text-cream mb-8" style={{textShadow: '4px 4px 0 #1a1a1a'}}>
          VS
        </div>
        <div className="bg-cream border-4 border-ink p-4 inline-block" style={{boxShadow: '8px 8px 0 #1a1a1a'}}>
          <BaseballDiamond bases={demoState.bases} size="large" />
        </div>
      </div>

      {/* Right - Batting Team */}
      <div className="matchup-card p-8 text-center w-80">
        <p className="retro-body text-xl text-grass mb-2">AT BAT</p>
        <p className="retro-heading text-3xl text-grass mb-2">
          {demoState.currentBatter}
        </p>
        <p className="retro-body text-lg text-grass mb-4">
          ({demoState.half === 'top' ? demoState.team_a.name : demoState.team_b.name})
        </p>
        <div className="w-24 h-24 mx-auto bg-grass rounded-full flex items-center justify-center border-4 border-ink">
          <span className="text-4xl">🏏</span>
        </div>
      </div>
    </div>
  );

  // ========== SONG PLAYING ==========
  const renderSongPlaying = () => {
    const pitchingTeam = demoState.half === 'top' ? demoState.team_b : demoState.team_a;
    const battingTeam = demoState.half === 'top' ? demoState.team_a : demoState.team_b;
    
    return (
      <div className="flex-1 flex items-center justify-center gap-12 p-8">
        {/* Left - Diamond */}
        <div className="bg-cream border-4 border-ink p-6" style={{boxShadow: '8px 8px 0 #1a1a1a'}}>
          <BaseballDiamond bases={demoState.bases} size="large" />
        </div>

        {/* Center - Timer & Status */}
        <div className="text-center">
          {/* Spinning Baseball with Timer */}
          <div className="relative inline-block mb-6">
            <div 
              className="w-48 h-48 flex items-center justify-center"
              style={{ animation: 'spin 1s linear infinite' }}
            >
              <svg viewBox="0 0 100 100" width="192" height="192" style={{ filter: 'drop-shadow(4px 4px 4px rgba(0,0,0,0.3))' }}>
                <defs>
                  <radialGradient id="tvBallGradient" cx="35%" cy="35%" r="60%">
                    <stop offset="0%" stopColor="#ffffff" />
                    <stop offset="50%" stopColor="#f5f5f5" />
                    <stop offset="100%" stopColor="#e0e0e0" />
                  </radialGradient>
                </defs>
                <circle cx="50" cy="50" r="46" fill="url(#tvBallGradient)" stroke="#333" strokeWidth="3"/>
                <path d="M 25 20 Q 5 50 25 80" fill="none" stroke="#CC0000" strokeWidth="3" strokeLinecap="round"/>
                <g stroke="#CC0000" strokeWidth="2" strokeLinecap="round">
                  <line x1="22" y1="25" x2="28" y2="28" />
                  <line x1="18" y1="35" x2="25" y2="36" />
                  <line x1="16" y1="45" x2="23" y2="45" />
                  <line x1="16" y1="55" x2="23" y2="55" />
                  <line x1="18" y1="65" x2="25" y2="64" />
                  <line x1="22" y1="75" x2="28" y2="72" />
                </g>
                <path d="M 75 20 Q 95 50 75 80" fill="none" stroke="#CC0000" strokeWidth="3" strokeLinecap="round"/>
                <g stroke="#CC0000" strokeWidth="2" strokeLinecap="round">
                  <line x1="78" y1="25" x2="72" y2="28" />
                  <line x1="82" y1="35" x2="75" y2="36" />
                  <line x1="84" y1="45" x2="77" y2="45" />
                  <line x1="84" y1="55" x2="77" y2="55" />
                  <line x1="82" y1="65" x2="75" y2="64" />
                  <line x1="78" y1="75" x2="72" y2="72" />
                </g>
              </svg>
            </div>
            {/* Timer overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-ink/80 rounded-full w-20 h-20 flex items-center justify-center">
                <span className="retro-heading text-3xl text-white">{timeRemaining}</span>
              </div>
            </div>
          </div>
          
          <p className="retro-heading text-2xl text-cream mb-4" style={{textShadow: '2px 2px 0 #1a1a1a'}}>
            🎵 NOW PLAYING
          </p>
          
          {/* Progress Bar */}
          <div className="w-80 h-6 bg-ink border-4 border-ink mx-auto">
            <div 
              className="h-full transition-all duration-1000"
              style={{ 
                width: `${((30 - timeRemaining) / 30) * 100}%`,
                backgroundColor: '#00ff00',
                boxShadow: '0 0 10px rgba(0, 255, 0, 0.5)'
              }}
            />
          </div>
        </div>

        {/* Right - Batting & Pitching Info - Solid cream boxes */}
        <div className="space-y-6" data-testid="matchup-info">
          {/* NOW BATTING - Solid cream box */}
          <div 
            className="bg-cream border-4 border-grass p-6 w-80"
            style={{boxShadow: '6px 6px 0 #1a1a1a'}}
            data-testid="now-batting"
          >
            <p className="retro-heading text-xl text-grass mb-2">NOW BATTING</p>
            <p className="retro-heading text-4xl text-grass">{demoState.currentBatter}</p>
            <p className="retro-body text-xl text-grass/70">({battingTeam.name})</p>
          </div>
          
          {/* NOW PITCHING - Solid cream box */}
          <div 
            className="bg-cream border-4 border-dirt p-6 w-80"
            style={{boxShadow: '6px 6px 0 #1a1a1a'}}
            data-testid="now-pitching"
          >
            <p className="retro-heading text-xl text-dirt mb-2">NOW PITCHING</p>
            <p className="retro-heading text-4xl text-dirt">{pitchingTeam.name}</p>
          </div>
        </div>
      </div>
    );
  };

  // ========== RESULT SCREENS (Jumbotron Grid Layout) ==========
  const renderResult = (type) => {
    const resultConfig = {
      'result_out': { text: 'OUT!', color: '#ff3b30', bgGlow: 'rgba(255,59,48,0.2)', runs: 0, shake: true, emoji: '❌' },
      'result_single': { text: 'SINGLE!', color: '#ffcc00', bgGlow: 'rgba(255,204,0,0.15)', runs: 0, shake: false, emoji: '1️⃣' },
      'result_double': { text: 'DOUBLE!', color: '#007aff', bgGlow: 'rgba(0,122,255,0.15)', runs: 1, shake: false, emoji: '2️⃣' },
      'result_triple': { text: 'TRIPLE!', color: '#af52de', bgGlow: 'rgba(175,82,222,0.15)', runs: 2, shake: false, emoji: '3️⃣' },
      'result_homerun': { text: 'HOME RUN!', color: '#00ff00', bgGlow: 'rgba(0,255,0,0.2)', runs: 3, shake: true, fireworks: true, emoji: '💥' },
      'result_grandslam': { text: 'GRAND SLAM!', color: '#ff00ff', bgGlow: 'rgba(255,0,255,0.3)', runs: 4, shake: true, fireworks: true, emoji: '🎆' },
    };
    
    const config = resultConfig[type];
    const isOut = type === 'result_out';
    const isGrandSlam = type === 'result_grandslam';
    
    // New base positions after this result (demo data)
    const newBases = (type === 'result_homerun' || type === 'result_grandslam') 
      ? { first: false, second: false, third: false }
      : type === 'result_out'
        ? demoState.bases
        : { first: true, second: type !== 'result_single', third: type === 'result_triple' };
    
    return (
      <div className={`flex-1 flex flex-col relative ${config.shake ? 'screen-shake' : ''}`}>
        {/* Fireworks for HOME RUN */}
        {config.fireworks && showFireworks && <Fireworks />}
        
        {/* Background glow effect */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse at center, ${config.bgGlow} 0%, transparent 70%)` }}
        />
        
        {/* ===== MAIN STAGE (60vh) - Result & Runs ===== */}
        <div className="flex-1 flex items-center justify-center relative z-10 py-8">
          <div className="flex items-center gap-16">
            
            {/* Left Side - Diamond with updated positions */}
            <div className="hidden lg:block">
              <div className="bg-cream/10 border-4 border-cream/30 p-6 backdrop-blur-sm">
                <BaseballDiamond bases={newBases} size="large" />
                {!isOut && config.runs > 0 && (
                  <p className="retro-body text-xl text-center mt-4" style={{ color: config.color }}>
                    +{config.runs} RUN{config.runs > 1 ? 'S' : ''}
                  </p>
                )}
              </div>
            </div>
            
            {/* Center - Main Result */}
            <div className="text-center result-blast">
              {/* Result Type */}
              <div 
                className="retro-heading text-7xl md:text-9xl mb-6"
                style={{ 
                  color: config.color,
                  textShadow: `0 0 40px ${config.color}, 0 0 80px ${config.color}, 0 0 120px ${config.color}, 6px 6px 0 #1a1a1a`,
                  letterSpacing: '0.05em'
                }}
                data-testid="result-text"
              >
                {config.text}
              </div>
              
              {/* Runs Banner - Only for hits with runs */}
              {config.runs > 0 && (
                <div className="mb-6 animate-pulse">
                  <div 
                    className="inline-block px-10 py-5 border-4 border-ink"
                    style={{
                      backgroundColor: config.color,
                      boxShadow: `8px 8px 0 #1a1a1a, 0 0 30px ${config.color}`
                    }}
                  >
                    <p className="retro-heading text-4xl md:text-5xl text-ink">
                      +{config.runs} RUN{config.runs > 1 ? 'S' : ''}!
                    </p>
                  </div>
                </div>
              )}
              
              {/* Updated Score Display */}
              <div className="flex justify-center gap-8 mt-4">
                <div className="text-center">
                  <p className="retro-body text-xl text-cream/70">{demoState.team_a.name}</p>
                  <p className="retro-heading text-5xl" style={{ color: '#00ff00', textShadow: '0 0 15px #00ff00' }}>
                    {demoState.team_a.score + (config.runs > 0 && demoState.half === 'top' ? config.runs : 0)}
                  </p>
                </div>
                <p className="retro-heading text-4xl text-cream self-end mb-2">-</p>
                <div className="text-center">
                  <p className="retro-body text-xl text-cream/70">{demoState.team_b.name}</p>
                  <p className="retro-heading text-5xl" style={{ color: '#00ff00', textShadow: '0 0 15px #00ff00' }}>
                    {demoState.team_b.score + (config.runs > 0 && demoState.half === 'bottom' ? config.runs : 0)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Right Side - At Bat Info */}
            <div className="hidden lg:block text-right">
              <p className="retro-body text-xl text-cream/50 mb-2">AT BAT</p>
              <p className="retro-heading text-3xl text-cream mb-1">{demoState.currentBatter}</p>
              <p className="retro-body text-lg text-cream/70">
                ({demoState.half === 'top' ? demoState.team_a.name : demoState.team_b.name})
              </p>
            </div>
          </div>
        </div>
        
        {/* ===== BOTTOM DECK (25vh) - Song Reveal ===== */}
        <div className="bg-black/50 border-t-4 border-cream/20 p-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-8">
              {/* Vinyl Record Icon */}
              <div className="relative flex-shrink-0">
                <div 
                  className="w-28 h-28 rounded-full border-4 border-ink flex items-center justify-center"
                  style={{
                    background: 'conic-gradient(from 0deg, #1a1a1a, #333, #1a1a1a, #333, #1a1a1a)',
                    animation: 'spin-vinyl 3s linear infinite'
                  }}
                >
                  <div className="w-10 h-10 rounded-full bg-red-500 border-2 border-ink flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-ink"></div>
                  </div>
                </div>
              </div>
              
              {/* Song Info */}
              <div className="flex-1">
                <p className="retro-body text-lg text-cream/50 mb-2">THE SONG WAS:</p>
                <p className="retro-heading text-3xl md:text-4xl text-cream mb-2">Baby Got Back</p>
                <p className="retro-body text-2xl text-cream/80">by Sir Mix-A-Lot</p>
              </div>
              
              {/* Album & Year */}
              <div className="text-right hidden md:block">
                <p className="retro-body text-xl text-cream/60">Mack Daddy</p>
                <p className="retro-heading text-2xl text-gold">1992</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ========== INNING SUMMARY (Web App Aesthetic) ==========
  const renderInningSummary = () => {
    // Mock inning-by-inning data for the box score
    const inningScores = {
      team_a: [0, 1, 1], // Runs per inning
      team_b: [1, 0, 0]
    };
    
    // Mock stats for this inning
    const inningStats = {
      runs: demoState.half === 'top' ? 1 : 0,
      hits: { singles: 2, doubles: 1, triples: 0, homeRuns: 0 },
      totalHits: 3
    };
    
    // Due up batters (next team)
    const nextTeam = demoState.half === 'top' ? demoState.team_b : demoState.team_a;
    const dueUpBatters = [
      { name: 'JOHNSON', avg: '.312' },
      { name: 'MARTINEZ', avg: '.285' },
      { name: 'WILLIAMS', avg: '.298' }
    ];
    
    const halfText = demoState.half === 'top' ? 'TOP' : 'BOTTOM';
    
    return (
      <div 
        className="flex-1 flex flex-col"
        style={{ background: 'linear-gradient(180deg, #4a7c4e 0%, #3d6b41 100%)' }}
        data-testid="inning-summary-screen"
      >
        {/* Content */}
        <div className="flex-1 flex flex-col p-8">
          
          {/* ===== HEADER BANNER ===== */}
          <div className="text-center mb-8" data-testid="inning-summary-header">
            <div 
              className="inline-block bg-cream border-4 border-ink px-12 py-4"
              style={{boxShadow: '8px 8px 0 #1a1a1a'}}
            >
              <h1 className="retro-heading text-4xl md:text-5xl text-ink">
                END OF {halfText} {demoState.inning}
              </h1>
            </div>
          </div>
          
          {/* ===== MAIN CONTENT - Two Column Layout ===== */}
          <div className="flex-1 flex gap-8">
            
            {/* LEFT SIDE (60%) - Line Score */}
            <div className="flex-[3] flex flex-col gap-4">
              
              {/* Box Score Table */}
              <div 
                className="bg-cream border-4 border-ink flex-1" 
                style={{boxShadow: '8px 8px 0 #1a1a1a'}}
                data-testid="line-score-table"
              >
                {/* Header */}
                <div className="bg-dirt p-4 border-b-4 border-ink">
                  <h2 className="retro-heading text-2xl text-cream">LINE SCORE</h2>
                </div>
                
                <div className="p-6">
                  {/* Table Header */}
                  <div className="grid grid-cols-7 gap-2 mb-4 pb-4 border-b-4 border-ink">
                    <div className="col-span-2">
                      <span className="retro-body text-xl text-dirt">TEAM</span>
                    </div>
                    {[1, 2, 3].map(inn => (
                      <div key={inn} className="text-center">
                        <span className={`retro-body text-xl ${inn === demoState.inning ? 'text-grass' : 'text-ink'}`}>
                          {inn}
                        </span>
                      </div>
                    ))}
                    <div className="text-center">
                      <span className="retro-heading text-xl text-grass">R</span>
                    </div>
                    <div className="text-center">
                      <span className="retro-heading text-xl text-dirt">H</span>
                    </div>
                  </div>
                  
                  {/* Team A Row */}
                  <div className="grid grid-cols-7 gap-2 mb-4 items-center">
                    <div className="col-span-2">
                      <span className="retro-heading text-2xl text-ink">{demoState.team_a.name}</span>
                    </div>
                    {inningScores.team_a.map((score, i) => (
                      <div key={i} className="text-center">
                        <span className={`retro-body text-3xl ${i + 1 === demoState.inning ? 'text-grass' : 'text-ink'}`}>
                          {score}
                        </span>
                      </div>
                    ))}
                    <div className="text-center">
                      <span className="retro-heading text-4xl text-grass">
                        {demoState.team_a.score}
                      </span>
                    </div>
                    <div className="text-center">
                      <span className="retro-body text-3xl text-ink">5</span>
                    </div>
                  </div>
                  
                  {/* Team B Row */}
                  <div className="grid grid-cols-7 gap-2 items-center">
                    <div className="col-span-2">
                      <span className="retro-heading text-2xl text-ink">{demoState.team_b.name}</span>
                    </div>
                    {inningScores.team_b.map((score, i) => (
                      <div key={i} className="text-center">
                        <span className={`retro-body text-3xl text-ink`}>
                          {i + 1 <= demoState.inning ? score : '-'}
                        </span>
                      </div>
                    ))}
                    <div className="text-center">
                      <span className="retro-heading text-4xl text-grass">
                        {demoState.team_b.score}
                      </span>
                    </div>
                    <div className="text-center">
                      <span className="retro-body text-3xl text-ink">3</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Inning Recap Stats */}
              <div 
                className="bg-cream border-4 border-ink p-6"
                style={{boxShadow: '8px 8px 0 #1a1a1a'}}
              >
                <h3 className="retro-body text-lg text-dirt mb-4">
                  {halfText} {demoState.inning} RECAP - {demoState.half === 'top' ? demoState.team_a.name : demoState.team_b.name}
                </h3>
                <div className="flex gap-12">
                  <div>
                    <p className="retro-body text-sm text-dirt">RUNS</p>
                    <p className="retro-heading text-5xl text-grass">
                      {inningStats.runs}
                    </p>
                  </div>
                  <div>
                    <p className="retro-body text-sm text-dirt">HITS</p>
                    <p className="retro-heading text-5xl text-ink">{inningStats.totalHits}</p>
                  </div>
                  <div className="flex gap-4">
                    <div className="text-center">
                      <p className="retro-body text-xs text-dirt">1B</p>
                      <p className="retro-body text-2xl text-ink">{inningStats.hits.singles}</p>
                    </div>
                    <div className="text-center">
                      <p className="retro-body text-xs text-dirt">2B</p>
                      <p className="retro-body text-2xl text-ink">{inningStats.hits.doubles}</p>
                    </div>
                    <div className="text-center">
                      <p className="retro-body text-xs text-dirt">3B</p>
                      <p className="retro-body text-2xl text-ink">{inningStats.hits.triples}</p>
                    </div>
                    <div className="text-center">
                      <p className="retro-body text-xs text-dirt">HR</p>
                      <p className="retro-body text-2xl text-ink">{inningStats.hits.homeRuns}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* RIGHT SIDE (40%) - Due Up */}
            <div className="flex-[2]">
              <div 
                className="bg-cream border-4 border-ink h-full" 
                style={{boxShadow: '8px 8px 0 #1a1a1a'}}
                data-testid="due-up-list"
              >
                {/* Header */}
                <div className="bg-grass p-4 border-b-4 border-ink">
                  <h2 className="retro-heading text-2xl text-cream">DUE UP</h2>
                </div>
                
                <div className="p-6">
                  <p className="retro-body text-xl text-ink mb-6">{nextTeam.name}</p>
                  
                  <div className="space-y-4">
                    {dueUpBatters.map((batter, i) => (
                      <div 
                        key={i} 
                        className={`flex items-center gap-4 p-4 border-4 ${
                          i === 0 ? 'border-grass bg-grass/10' : 'border-ink'
                        }`}
                      >
                        <div className="w-10 h-10 bg-dirt flex items-center justify-center">
                          <span className="retro-heading text-xl text-cream">{i + 1}</span>
                        </div>
                        <div className="flex-1">
                          <p className="retro-heading text-xl text-ink">{batter.name}</p>
                        </div>
                        <div className="text-right">
                          <p className="retro-body text-xs text-dirt">AVG</p>
                          <p className="retro-body text-xl text-grass">{batter.avg}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Coming up indicator */}
                  <div className="mt-6 text-center">
                    <div className="inline-block bg-grass/20 border-4 border-grass px-6 py-3">
                      <p className="retro-body text-lg text-grass">
                        ⚾ {nextTeam.name} COMING TO BAT
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ========== GAME OVER ==========
  const renderGameOver = () => {
    const winner = demoState.team_a.score > demoState.team_b.score 
      ? demoState.team_a.name 
      : demoState.team_b.score > demoState.team_a.score 
        ? demoState.team_b.name : 'TIE';

    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center result-blast">
          <h1 
            className="retro-heading text-7xl text-cream mb-12"
            style={{textShadow: '4px 4px 0 #1a1a1a, 0 0 30px rgba(255,255,255,0.3)'}}
          >
            FINAL
          </h1>
          
          {/* Score Display */}
          <div className="flex items-center justify-center gap-16 mb-12">
            <div className="text-center">
              <p className="retro-heading text-3xl text-cream mb-4" style={{textShadow: '2px 2px 0 #1a1a1a'}}>
                {demoState.team_a.name}
              </p>
              <div className="bg-black px-8 py-4 border-4 border-green-900">
                <span className="jumbotron-score text-8xl">{demoState.team_a.score}</span>
              </div>
            </div>
            
            <span className="retro-heading text-5xl text-cream" style={{textShadow: '3px 3px 0 #1a1a1a'}}>
              -
            </span>
            
            <div className="text-center">
              <p className="retro-heading text-3xl text-cream mb-4" style={{textShadow: '2px 2px 0 #1a1a1a'}}>
                {demoState.team_b.name}
              </p>
              <div className="bg-black px-8 py-4 border-4 border-green-900">
                <span className="jumbotron-score text-8xl">{demoState.team_b.score}</span>
              </div>
            </div>
          </div>
          
          {/* Winner Banner */}
          <div 
            className="inline-block bg-grass px-12 py-6 border-4 border-ink"
            style={{boxShadow: '8px 8px 0 #1a1a1a'}}
          >
            <p className="retro-heading text-4xl text-cream">
              {winner === 'TIE' ? "IT'S A TIE!" : `${winner} WINS!`}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // ========== BOTTOM TICKER ==========
  const renderTicker = () => (
    <div className="tv-ticker px-8 py-3 overflow-hidden">
      <p className="retro-body text-xl text-yellow-400 whitespace-nowrap">
        ⚾ RADIO BASEBALL • MUSIC TRIVIA GAME • {demoState.team_a.name} vs {demoState.team_b.name} • 
        {demoState.half === 'top' ? 'TOP' : 'BOTTOM'} OF INNING {demoState.inning} • 
        CONTROLLER CONNECTED ⚾
      </p>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* CRT Scanline Overlay */}
      <div className="crt-overlay"></div>
      
      {/* View Selector */}
      <div className="bg-ink p-3 flex gap-2 justify-center flex-wrap z-10">
        <span className="retro-body text-sm text-cream mr-4">TV DEMO:</span>
        {views.map(v => (
          <button
            key={v.id}
            onClick={() => setCurrentView(v.id)}
            className={`px-3 py-1 text-xs retro-body border-2 transition-all ${
              currentView === v.id 
                ? 'bg-grass text-cream border-grass' 
                : 'bg-transparent text-cream border-cream/50 hover:border-cream'
            }`}
            data-testid={`view-${v.id}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Main TV Display */}
      <div className="flex-1 flex flex-col tv-field-bg">
        {renderScoreboard()}
        
        {currentView === 'pitching_waiting' && renderPitchingWaiting()}
        {currentView === 'song_playing' && renderSongPlaying()}
        {currentView.startsWith('result_') && renderResult(currentView)}
        {currentView === 'inning_summary' && renderInningSummary()}
        {currentView === 'game_over' && renderGameOver()}
        
        {renderTicker()}
      </div>
    </div>
  );
}
